import { createFileRoute, Link } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

import { pageSchemas } from "@/lib/seo/page-schema";
export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Perceptica" },
      { name: "description", content: "How Perceptica handles personal data across our media monitoring platform and media intelligence services. Read our full privacy commitments." },
      { property: "og:title", content: "Privacy Policy | Perceptica" },
      { property: "og:description", content: "How Perceptica handles personal data across our media monitoring platform and media intelligence services. Read our full privacy commitments." },

      ...ogImageMeta("en", "/privacy-policy", "Perceptica Privacy Policy"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Privacy Policy | Perceptica" },
      { name: "twitter:description", content: "How Perceptica handles personal data across our media monitoring platform and media intelligence services. Read our full privacy commitments." },
      { property: "og:url", content: "https://perceptica.com/privacy-policy" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/privacy-policy" }),
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-20 lg:px-10 lg:py-28">
        <h1 className="font-display text-[clamp(2rem,3.4vw,2.8rem)] font-medium text-foreground">Privacy Policy</h1>
        <p className="mt-4 text-[14px] text-foreground/70">Last updated: August 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-[14.5px] leading-relaxed text-foreground/85">
          <p>
            Perceptica ("Perceptica", "we", "us") provides media monitoring and analysis services to
            communications, PR and marketing teams across Europe. This Privacy Policy explains what
            personal data we collect when you visit our website or use our services, why we process
            it, who we share it with, and the rights you have under the EU General Data Protection
            Regulation (GDPR) and the Bulgarian Personal Data Protection Act.
          </p>

          <h2 className="font-display mt-10 text-xl">1. Data controller</h2>
          <p>
            Perceptica is the controller of personal data processed through this website and through
            its monitoring platform when you are our customer or evaluate our service. You can reach
            our team about any privacy matter at{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
          </p>

          <h2 className="font-display mt-10 text-xl">2. Personal data we collect</h2>
          <ul className="mt-3 space-y-2">
            <li><strong>Contact and request data</strong> - the information you submit through our demo,
              free trial, contact and careers forms (name, work email, company, role, country, the
              message you write to us).</li>
            <li><strong>Account data</strong> - if you become a customer, the credentials, profile and
              configuration data needed to operate your workspace on the platform.</li>
            <li><strong>Usage data</strong> - product analytics about how the platform and website are
              used (pages viewed, features opened, errors). On the public website we only set
              non-essential analytics cookies after you give consent through the cookie banner.</li>
            <li><strong>Technical data</strong> - server and security logs (IP address, user agent,
              timestamps) needed to operate, secure and troubleshoot the service.</li>
            <li><strong>Cookies</strong> - see our{" "}
              <Link to="/cookie-policy" className="underline underline-offset-2">Cookie Policy</Link>{" "}
              for the categories we use and how you can change your choice at any time.</li>
          </ul>

          <h2 className="font-display mt-10 text-xl">3. Why we use your data and the legal basis</h2>
          <ul className="mt-3 space-y-2">
            <li><strong>To respond to you</strong> (demo, trial, contact, careers requests) - based on
              steps taken at your request prior to entering into a contract (Art. 6(1)(b) GDPR) and
              our legitimate interest in answering business enquiries (Art. 6(1)(f) GDPR).</li>
            <li><strong>To provide the Perceptica service</strong> to our customers - performance of
              our customer agreement (Art. 6(1)(b) GDPR).</li>
            <li><strong>To keep the service secure and reliable</strong> - our legitimate interest in
              protecting our infrastructure, customers and users (Art. 6(1)(f) GDPR).</li>
            <li><strong>To measure and improve the website</strong> - your consent for analytics and
              marketing cookies (Art. 6(1)(a) GDPR).</li>
            <li><strong>To meet legal obligations</strong> such as accounting, tax and responding to
              valid requests from authorities (Art. 6(1)(c) GDPR).</li>
          </ul>

          <h2 className="font-display mt-10 text-xl">4. Who we share data with</h2>
          <p>
            We do not sell personal data. We share it only with vetted service providers acting as
            our processors under written data processing agreements - for example cloud hosting,
            email delivery, customer support, product analytics and CRM tooling. Where required, we
            may also disclose data to regulators, courts or law enforcement when there is a valid
            legal basis to do so.
          </p>

          <h2 className="font-display mt-10 text-xl">5. International transfers</h2>
          <p>
            Perceptica is built around EU data residency. Customer data is stored in the European
            Union by default. Where a sub-processor processes data outside the EU/EEA, we rely on
            the European Commission's Standard Contractual Clauses and additional safeguards as
            required under Chapter V of the GDPR.
          </p>

          <h2 className="font-display mt-10 text-xl">6. How long we keep data</h2>
          <p>
            We keep personal data only for as long as needed for the purpose it was collected for.
            In practice:
          </p>
          <ul className="mt-3 space-y-2">
            <li>Demo, trial and contact requests - up to 24 months from the last interaction, then
              deleted or anonymised.</li>
            <li>Customer account data - for the duration of the contract and a short post-termination
              window for back-ups and dispute resolution.</li>
            <li>Server and security logs - typically up to 12 months.</li>
            <li>Records we are legally required to keep (for example invoices) - for the period set
              by the relevant law.</li>
          </ul>

          <h2 className="font-display mt-10 text-xl">7. Your rights</h2>
          <p>You have the right to:</p>
          <ul className="mt-3 space-y-2">
            <li>access the personal data we hold about you;</li>
            <li>have inaccurate data corrected or completed;</li>
            <li>have your data erased ("right to be forgotten") where the conditions are met;</li>
            <li>restrict or object to certain processing, including direct marketing;</li>
            <li>receive your data in a portable format;</li>
            <li>withdraw any consent you previously gave, without affecting prior processing;</li>
            <li>lodge a complaint with a supervisory authority - in Bulgaria the Commission for
              Personal Data Protection (CPDP, <a href="https://www.cpdp.bg" className="underline underline-offset-2" target="_blank" rel="noreferrer">cpdp.bg</a>),
              or with the authority in your country of residence or work.</li>
          </ul>
          <p className="mt-4">
            To exercise any of these rights, write to{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
            We will respond within the timeframes set by the GDPR.
          </p>

          <h2 className="font-display mt-10 text-xl">8. Security</h2>
          <p>
            We protect personal data with appropriate technical and organisational measures,
            including encryption in transit, role-based access controls, audit logging, regular
            back-ups and security reviews of our infrastructure and sub-processors.
          </p>

          <h2 className="font-display mt-10 text-xl">9. Cookies</h2>
          <p>
            Our website uses strictly necessary cookies and, with your consent, analytics and
            marketing cookies. You can review the categories and change your choice at any time on
            our <Link to="/cookie-policy" className="underline underline-offset-2">Cookie Policy</Link> page.
          </p>

          <h2 className="font-display mt-10 text-xl">10. Children</h2>
          <p>
            Perceptica is a B2B service. It is not directed at children under 16, and we do not
            knowingly collect their personal data. If you believe a child has shared data with us,
            please contact us so we can delete it.
          </p>

          <h2 className="font-display mt-10 text-xl">11. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy as our service or the law evolves. The current version
            is always published on this page with a new "Last updated" date. Material changes will
            be communicated through the website or, where appropriate, directly to our customers.
          </p>

          <h2 className="font-display mt-10 text-xl">12. Contact</h2>
          <p>
            For any question about this policy or how Perceptica handles personal data, contact us
            at <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
